import "@inovitas/infra3dsdk/dist/styles/css/infra3dsdk.css";
import "./homePage.css";

import { mm, Viewer } from "@inovitas/infra3dsdk";
import { Alert, Box, Link, Snackbar } from "@mui/material";
import { debounce } from "lodash";
import React, { useEffect, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import ClientConfig from "shared/config";
import { useSnackbar } from "shared/common/contexts/SnackbarProvider";
import UseAuth from "shared/common/hooks/UseAuth";
import {
  getPilegateURL,
  ProviderType,
} from "shared/common/provider/pilegateprovider";
import { getProviderType } from "shared/common/provider/userProviderDecision";
import { Manager } from "shared/common/services/manager";
import { defaultStore } from "shared/common/stores/defaultStore";
import DeleteFeature from "shared/common/utils/alerts/DeleteFeature";
import SaveChanges from "shared/common/utils/alerts/SaveChanges";
import SaveOrDiscardLastChanges from "shared/common/utils/alerts/SaveOrDiscardLastChanges";
import IntroSystem from "shared/common/utils/intro/IntroSystem";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import { addLayers } from "shared/common/utils/layers/addLayers";
import { addLinRefLayers } from "shared/common/utils/linref/addLinRefLayers";
import Loading from "shared/common/utils/loading/Loading";
import {
  getAuthorizationHeaders,
  getApiKeyHeaders,
} from "shared/common/utils/general-utils";
import { getLayersAddedByUser } from "../../common/utils/layers/localStorageLayers";
import { useSearchParams } from "react-router-dom";
import {
  CoordinateSchema,
  ImageKeySchemaUUID,
  ImageKeySchemaString,
  PointToSchema,
} from "../../common/schemas/searchParamSchema";

export interface projectConfig {
  geo_data: any;
  anon?: {
    project: string;
  };
  collection: string;
  image_key: string;
  map?: {
    show_segmented_only: boolean;
  };
  render_distance?: number;
  uid: string;
}

interface props {
  children: JSX.Element | JSX.Element[];
  map_expand?: boolean;
  manager?: Manager;
  project_uid?: string;
  role: "api" | "edit" | "view";
  show_cockpit?: boolean;
  show_mapWindow?: boolean;
  show_toolbar?: boolean;
  show_topbar?: boolean;
  start_campaign?: { id: string };
  start_position?: { easting: number; northing: number; epsg?: number };
  authMode: "view" | "edit";
}

enum framer_version {
  v2 = "2",
  v3 = "3",
}

export default function HomePage({
  children,
  manager,
  map_expand,
  project_uid,
  role,
  show_cockpit,
  show_mapWindow,
  show_toolbar,
  show_topbar,
  start_campaign,
  start_position,
  authMode,
}: props) {
  const { userData, tokens } = UseAuth();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const projectConfig = defaultStore((state) => state.projectConfig);
  const changeProjectConfig = defaultStore(
    (state) => state.changeProjectConfig
  );
  const clientConfig = defaultStore((state) => state.clientConfig);
  const changeClientConfig = defaultStore((state) => state.changeClientConfig);
  const viewer = defaultStore((state) => state.viewer);
  const changeViewer = defaultStore((state) => state.changeViewer);
  const changeDarkTheme = defaultStore((state) => state.changeDarkTheme);
  const mapExpand = defaultStore((state) => state.mapExpand);
  const changeMapExpand = defaultStore((state) => state.changeMapExpand);
  const changeManager = defaultStore((state) => state.changeManager);
  const changeShowCockpit = defaultStore((state) => state.changeShowCockpit);
  const changeShowMapWindow = defaultStore(
    (state) => state.changeShowMapWindow
  );
  const changeShowToolbar = defaultStore((state) => state.changeShowToolbar);
  const changeShowTopbar = defaultStore((state) => state.changeShowTopbar);
  const showStreetMap = defaultStore((state) => state.showStreetMap);
  const loading = defaultStore((state) => state.loading);
  const changeLoading = defaultStore((state) => state.changeLoading);
  const changeLayerConfiguration = defaultStore(
    (state) => state.changeLayerConfiguration
  );
  const layersToAdd = defaultStore((state) => state.layersToAdd);
  const changeLayersToAdd = defaultStore((state) => state.changeLayersToAdd);
  const alertMessage = defaultStore((state) => state.alertMessage);
  const changeAlertMessage = defaultStore((state) => state.changeAlertMessage);
  const alertDeleteFeature = defaultStore((state) => state.alertDeleteFeature);
  const alertSaveLayer = defaultStore((state) => state.alertSaveLayer);
  const alertSaveOrDiscardLastLayer = defaultStore(
    (state) => state.alertSaveOrDiscardLastLayer
  );
  const apm = defaultStore((state) => state.apm);
  const providerType = defaultStore((state) => state.providerType);
  const changeProviderType = defaultStore((state) => state.changeProviderType);
  const changeAxisCollection = defaultStore(
    (state) => state.changeAxisCollection
  );
  const changeNetworkLayer = defaultStore((state) => state.changeNetworkLayer);
  const changeShowAxisscale = defaultStore(
    (state) => state.changeShowAxisscale
  );
  const changeAuthMode = defaultStore((state) => state.changeAuthMode);

  const { showMessage } = useSnackbar();

  const parsePointTo = (searchParameter: string) => {
    const attempts = [
      () => JSON.parse(decodeURI(searchParameter)),
      () => JSON.parse(decodeURI(window.atob(searchParameter))),
    ];
    for (const attempt of attempts) {
      try {
        return PointToSchema.parse(attempt());
      } catch {
        continue;
      }
    }
    changeAlertMessage({
      message: `${t("alert_error.search_parameters.invalid_format")}`,
      type: "error",
    });
    return;
  };

  const parseCoordinate = (searchParameter: string) => {
    const attempts = [
      () => JSON.parse(decodeURI(searchParameter)),
      () => JSON.parse(decodeURI(window.atob(searchParameter))),
    ];
    for (const attempt of attempts) {
      try {
        return CoordinateSchema.parse(attempt());
      } catch {
        continue;
      }
    }
    changeAlertMessage({
      message: `${t("alert_error.search_parameters.invalid_format")}`,
      type: "error",
    });
    return;
  };

  const parseImageKey = (searchParameter: string) => {
    const attempts = [
      () => decodeURI(searchParameter),
      () => window.atob(searchParameter),
    ];
    // first check if it is a valid UUID
    for (const attempt of attempts) {
      try {
        return ImageKeySchemaUUID.parse(attempt());
      } catch {
        continue;
      }
    }

    // if not, check if it is at least valid string (as the switch to nextgen in classic just provides a string)
    for (const attempt of attempts.reverse()) {
      try {
        return ImageKeySchemaString.parse(attempt());
      } catch {
        continue;
      }
    }
    changeAlertMessage({
      message: `${t("alert_error.search_parameters.invalid_format")}`,
      type: "error",
    });
    return;
  };

  useEffect(() => {
    changeAuthMode(authMode);
  }, [authMode]);

  useEffect(() => {
    if (!userData) return;
    if (manager) {
      changeManager(manager);
    }
    changeMapExpand(
      map_expand !== undefined
        ? map_expand
        : window.innerWidth >= 900 //only open the map by default when desktop
          ? true
          : false
    );
    changeShowCockpit(show_cockpit !== undefined ? show_cockpit : true);
    changeShowMapWindow(show_mapWindow !== undefined ? show_mapWindow : true);
    changeShowToolbar(show_toolbar !== undefined ? show_toolbar : true);
    changeShowTopbar(show_topbar !== undefined ? show_topbar : true);
    changeProviderType(getProviderType(userData.userpool_id));
  }, []);

  //alert message state
  const [alertMessageState, setAlertMessageState] = useState<boolean>(false);

  // alert messages as a snackbar
  useEffect(() => {
    if (alertMessage) {
      setAlertMessageState(true);
    }
  }, [alertMessage]);

  const viewContainer = (
    <Box
      id="infra3d-left-container"
      position={"absolute"}
      left="0"
      width={"100%"}
      height="100%"
    >
      <Box width="100%" height="100%" className="infra3d" id="infra3d"></Box>
    </Box>
  );

  const mapContainer = (
    <Box
      position={"absolute"}
      left="0"
      width={"100%"}
      height="100%"
      id="infra3d-right-container"
    >
      <Box
        width={"100%"}
        height={"100%"}
        className="infra3dmap"
        id="infra3dmap"
      ></Box>
    </Box>
  );

  //set darkTheme into state after getting it from local storage
  useEffect(() => {
    if (localStorage.getItem("theme") === "dark") {
      changeDarkTheme(true);
    } else {
      changeDarkTheme(false);
    }
  }, []);

  //start view in this point if have the right path
  const init_startcollection = () => {
    if (start_position) {
      viewer
        ?.movePosition(
          {
            northing: start_position.northing,
            easting: start_position.easting,
          },
          start_position.epsg || 4326
        )
        .then(() => {
          if (start_campaign) {
            viewer?.moveToCampaign(start_campaign.id);
          }
        })
        .catch((error) => {
          changeAlertMessage({
            message: t("alert_error.missing_image"),
            type: "error",
          });
        });
      return;
    }
    if (!projectConfig) return;
    let imageKey = searchParams.get("imageKey");
    let pointTo = searchParams.get("pointTo");
    let jumpTo = searchParams.get("jumpTo");
    let lookAt = searchParams.get("lookAt");

    if ([imageKey, pointTo, jumpTo, lookAt].filter(Boolean).length > 2) {
      changeAlertMessage({
        message: t("alert_error.search_parameters.too_many"),
        type: "error",
      });
      return;
    }

    if (
      (imageKey && jumpTo) ||
      (imageKey && lookAt) ||
      (pointTo && jumpTo) ||
      (pointTo && lookAt) ||
      (jumpTo && lookAt)
    ) {
      changeAlertMessage({
        message: t("alert_error.search_parameters.invalid_combination"),
        type: "error",
      });
      return;
    }

    // parse imageKey search param in url
    if (imageKey) {
      imageKey = parseImageKey(imageKey) || null;
      if (!imageKey) return;
    } else {
      // fallback when no imageKey
      if (providerType === ProviderType.PILE) {
        imageKey = `${projectConfig.collection}/${projectConfig.start_up_frame}`;
      } else if (providerType === ProviderType.PILEGATE) {
        imageKey = projectConfig.start_up_frame;
      } else {
        imageKey = "";
      }
    }

    if (!imageKey) {
      changeAlertMessage({
        message: t("alert_error.missing_key"),
        type: "error",
      });
      return;
    }

    // default behaviour, no parameters or just imageKey
    if (!pointTo && !jumpTo && !lookAt) {
      viewer
        ?.moveToKey(imageKey)
        .then(() => {
          // Initialize the api on the imageKey, but on a specific campaign
          if (start_campaign) {
            viewer?.moveToCampaign(start_campaign.id);
          }
        })
        .then(() => {
          removeSearchParams();
        })
        .catch((err) => {
          changeAlertMessage({
            message: `${t("alert_error.search_parameters.move_to_key_error")}}`,
            type: "error",
          });
        });
      return;
    }

    // parse pointTo search param in url
    if (pointTo) {
      const pointToParsed = parsePointTo(pointTo);
      if (!pointToParsed) return;
      viewer
        ?.moveToKey(imageKey, pointToParsed)
        .then(() => {
          removeSearchParams();
        })
        .catch((err) => {
          changeAlertMessage({
            message: `${t("alert_error.search_parameters.move_to_key_error")}`,
            type: "error",
          });
        });
      return;
    }

    // parse jumpTo search param in url
    if (jumpTo) {
      const jumpToParsed = parseCoordinate(jumpTo);
      if (!jumpToParsed) return;
      const northing = jumpToParsed.northing;
      const easting = jumpToParsed.easting;
      const epsg = jumpToParsed.epsg;
      viewer
        ?.movePosition(
          {
            northing: northing,
            easting: easting,
          },
          epsg
        )
        .then(() => {
          removeSearchParams();
        })
        .catch((err) => {
          changeAlertMessage({
            message: `${t("alert_error.search_parameters.move_position_error")}`,
            type: "error",
          });
        });
      return;
    }

    // parse lookAt search param in url
    if (lookAt) {
      const lookAtParsed = parseCoordinate(lookAt);
      if (!lookAtParsed) return;
      const northing = lookAtParsed.northing;
      const easting = lookAtParsed.easting;
      const height = lookAtParsed.height;
      const epsg = lookAtParsed.epsg;
      viewer
        ?.movePositionLookAtPosition(
          {
            northing: northing,
            easting: easting,
            height: height,
          },
          100,
          epsg
        )
        .then(() => {
          removeSearchParams();
        })
        .catch((err) => {
          changeAlertMessage({
            message: `${t("alert_error.search_parameters.move_position_error")}`,
            type: "error",
          });
        });
      return;
    }
  };

  function removeSearchParams() {
    // remove search parameters from url
    setSearchParams((prev) => {
      prev.delete("imageKey");
      prev.delete("pointTo");
      prev.delete("jumpTo");
      prev.delete("lookAt");
      return prev;
    });
  }

  // add all layers from local storage
  const addLayersFromLocalStorage = () => {
    const allLayersFromUser = getLayersAddedByUser();
    if (viewer && allLayersFromUser.length > 0) {
      allLayersFromUser.forEach((source) => {
        if (source.sourceoptions) {
          source.source_options = JSON.parse(
            JSON.stringify(source.sourceoptions)
          );
          delete source.sourceoptions;
        }
      });
      addLayers(viewer, allLayersFromUser);
    }
  };

  //close snackbar message after 0.5 seconds
  const closeSnackbar = () => {
    setAlertMessageState(false);
  };

  const debouncedResize = debounce((viewer) => {
    viewer.resize();
  }, 100);

  //set authobject after getting token
  useEffect(() => {
    if (!viewer || !tokens) return;
    viewer.setAuthObject({
      apiKey: process.env.API_KEY || "",
      accessToken: tokens.access_token,
    });
  }, [tokens]);

  //get and save layer configuration from pile
  useEffect(() => {
    if (
      !userData ||
      !tokens ||
      !tokens.access_token ||
      (projectConfig && clientConfig) ||
      !providerType
    )
      return;

    // Get the configurations from pile or pilegate
    if (!project_uid) {
      const projectUIDParam = searchParams.get("projectUID");
      if (!projectUIDParam) {
        // Handle missing projectUID parameter
        changeAlertMessage({
          message: t("alert_error.missing_project_uid"),
          type: "error",
        });
        changeLoading(false);
        return;
      }
      project_uid = projectUIDParam;
    }

    if (providerType === ProviderType.PILE) {
      const pile_url = `${ClientConfig.pileURL}${encodeURIComponent(
        userData.tenant
      )}/${encodeURIComponent(userData.usergroups[0])}/${encodeURIComponent(
        userData.username
      )}/${authMode}-configs/${project_uid}`;

      fetch(pile_url, {
        headers: {
          ...getAuthorizationHeaders(tokens.access_token),
          ...getApiKeyHeaders(),
        },
      })
        .then((res) => {
          if (!res.ok) {
            changeAlertMessage({
              message: t("alert_error.missing_project_uid"),
              type: "error",
            });
            changeLoading(false);
            return;
          }
          return res.json();
        })
        .then((res) => {
          const proj_config = {
            collection: res.collection,
            start_up_frame: res.image_key,
            created_at: "",
            created_by: "",
            description: "",
            name: "",
            scope: undefined,
            tags: undefined,
            uid: res.uid,
          };
          changeProjectConfig(proj_config);
          const cli_config = {
            map: res.map,
            uid: res.uid,
            epsg_height: res.epsg_height,
            epsg: res.epsg,
            render_distance: res.render_distance,
            anon_project: res.anon.project,
            framer_version: framer_version.v2,
          };
          changeClientConfig(cli_config);
          const layer_config = res.geo_data;
          layer_config.forEach((source: any) => {
            if (source.sourceoptions) {
              source.source_options = JSON.parse(
                JSON.stringify(source.sourceoptions)
              );
              delete source.sourceoptions;
            }
          });
          changeLayerConfiguration(layer_config);
          changeLayersToAdd(layer_config);
        })
        .catch((err) => {
          console.error(err);
          changeAlertMessage({
            message: t("alert_error.missing_project_uid"),
            type: "error",
          });
          changeLoading(false);
        });
    } else if (providerType === ProviderType.PILEGATE) {
      const pilegate_project_url = getPilegateURL(
        userData.userpool_id,
        `${project_uid}`
      );
      fetch(pilegate_project_url, {
        headers: {
          ...getAuthorizationHeaders(tokens.access_token),
          ...getApiKeyHeaders(),
        },
      })
        .then((res) => {
          if (!res.ok) {
            changeAlertMessage({
              message: t("alert_error.missing_project_uid"),
              type: "error",
            });
            changeLoading(false);
            return;
          }
          return res.json();
        })
        .then((res) => {
          changeProjectConfig(res);
        })
        .catch((err) => {
          console.error(err);
          changeAlertMessage({
            message: t("alert_error.missing_project_uid"),
            type: "error",
          });
          changeLoading(false);
        });

      const pilegate_client_url = getPilegateURL(
        userData.userpool_id,
        `${project_uid}/client-configs/?client=${authMode}`
      );
      fetch(pilegate_client_url, {
        headers: {
          ...getAuthorizationHeaders(tokens.access_token),
          ...getApiKeyHeaders(),
        },
      })
        .then((res) => {
          if (!res.ok) {
            changeAlertMessage({
              message: t("alert_error.missing_project_uid"),
              type: "error",
            });
            changeLoading(false);
            return;
          }
          return res.json();
        })
        .then((res) => {
          if (res.value.length === 0) {
            changeAlertMessage({
              message: "There was no client configuration found!",
              type: "error",
            });
          }
          res.value[0].framer_version = framer_version.v3;
          changeClientConfig(res.value[0]);
        })
        .catch((err) => {
          console.error(err);
          changeAlertMessage({
            message: t("alert_error.missing_project_uid"),
            type: "error",
          });
          changeLoading(false);
        });

      const pilegate_layer_url = getPilegateURL(
        userData.userpool_id,
        `${project_uid}/layer-configs/?client=${authMode}`
      );
      fetch(pilegate_layer_url, {
        headers: {
          ...getAuthorizationHeaders(tokens.access_token),
          ...getApiKeyHeaders(),
        },
      })
        .then((res) => {
          if (!res.ok) {
            changeAlertMessage({
              message: t("alert_error.missing_project_uid"),
              type: "error",
            });
            changeLoading(false);
            return;
          }
          return res.json();
        })
        .then((res) => {
          changeLayerConfiguration(res.value);
          changeLayersToAdd(res.value);
        })
        .catch((err) => {
          console.error(err);
          changeAlertMessage({
            message:
              "There was an error while loading the Layer-Configurations!",
            type: "error",
          });
        });
    }
  }, [userData, tokens, providerType]);

  //set Viewer after getting project congifurations
  useEffect(() => {
    if (!projectConfig) return;
    if (!clientConfig) return;
    if (!tokens) return;
    const accessToken = tokens.access_token;

    const _viewer = new Viewer({
      container: "infra3d",
      apiClient: "",
      component: {
        sequence: false,
        axisscalebar: false,
        direction: true,
        cache: false,
        map:
          show_mapWindow === false
            ? false
            : {
                hidecontrols: true,
                container: "infra3dmap",
                baselayers: clientConfig.map?.base_layers,
                overlaylayers: clientConfig.map?.overlay_layers,
                overviewsegmentedonly: clientConfig.map?.show_segmented_only,
                routesconfig: {
                  routes: [
                    {
                      loa: 0,
                      max_zoom: 100,
                      min_zoom: 19,
                    },
                  ],
                  route_lines: [
                    {
                      loa: 0,
                      max_zoom: 19,
                      min_zoom: 17,
                    },
                    {
                      loa: 1,
                      max_zoom: 17,
                      min_zoom: 13,
                    },
                  ],
                  route_hexes: [
                    {
                      loa: 0,
                      max_zoom: 13,
                      min_zoom: 11,
                    },
                    {
                      loa: 1,
                      max_zoom: 11,
                      min_zoom: 9,
                    },
                    {
                      loa: 2,
                      max_zoom: 9,
                      min_zoom: 0,
                    },
                  ],
                },
                locatorIcon:
                  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACgAAAAoCAYAAACM/rhtAAAABmJLR0QA/wD/AP+gvaeTAAADBklEQVRYw+2YX2iPURjHP+cls1/zZ/N3/m21GyYrFy4kuSDMdjH/c4MiSsxIUijJjUaK7YLIhcwW8n8uhFEkMVuKsLLNmsnGRvvZmp2vi/d98yNsNu/8Ln7fOhfvOed5zqfnPc9zzvvCP5SkMZLGElMPZK01kr7IVUI0Au6T7ZQ6v8paWxhtcGmSpIs7pZJcL4jKiBpASWelDmlVnLQEqb1Zkm5ES/TmS5KKd0iZSAuQTqz3o7gsGqJXqXcvpRVIq5HWIC1GqimXpOre+nd6CZcHZHB6O3wC4oEBQAdwaitAirV2T2/WML2AGwLUU3ktxK5sSAasN9gPeAPsLoLpKwDGG2Pq+jSCkvYBIQpy4R1uBDu91gI0AwUbgXYk7e/rxEiXJJ1cK61CunVIqn4ktbx1W225dKdAWmOkwqV+wkzry8Q4p9YP0sPTku3UH/WwyIXuYdkxPYCbBZQhC8YBa+H2EXhcAo2v3UlJKTA1B2bnQf+B+HMl5TiOcylowPvAdABe34P8OfCqDRKAkOcx7O3JVAPbrsPEeb75M2PM5MAAJS0HigGougl5cyAOGO0lR6T6Ae+9hDlwHqYs8kfWG2OOBQVYCWRAO6wbB02Nv4aLrBFNQHwIjtZBXCJAjTEmNagykwzAlcNQ3QUcXl0cDtSH4dJBv3d0kHXQxXl6FQZ1ARdpkejZRPoICNC9iDa+cBOiu4oHmqqgLew/BQbYigCpB/lv3XIDbUECvsUAQ1Phy19YtXs28Ql4p3QwgJIeAJA+1y0fTjdXaAImzvV9VAQZwRIAsjbDqAHwsQsPBvgMDAWyNvm9ZwIDdBynDCgjNBxyj38vxM5vPLcCdcDGAhiWBvDEcZyLgR511to0Y0wVAI9PQf5K9xWOwD1V/D3XCAwGthTCjA2++VRjTEWggN4+mgncBeBTA1zdC5UXoLnBnTB4JEzJguw9kDTBt8lyHKe0L69cKZKe/3C1CjdL4Y8/X7hqrbWT/udXXaaky5LqI6AaJJVaaxfG/rnEFFNMMcUU0x/1Dbk14fovKBg6AAAAAElFTkSuQmCC",
              },
      },
      jumpMode: "similar",

      imageProvider: {
        host: ClientConfig.stargateURL,
      },
      lidarProvider: {
        host: ClientConfig.lidargateURL,
      },
      frameProvider: {
        host:
          providerType === ProviderType.PILE
            ? ClientConfig.framerURL
            : ClientConfig.framegateURL,
        collection: projectConfig.collection,
        framer_version: clientConfig.framer_version,
      },
      frameFilter: [
        "frame.capturesystem.orientation",
        "frame.capturesystem.sensorgroup",
        "frame.campaignID",
      ],
      authObject: {
        apiKey: process.env.API_KEY || "",
        accessToken: accessToken,
      },
      authMode: authMode,
      apm: apm,
      epsg: providerType === ProviderType.PILE ? undefined : clientConfig.epsg,
      projectScope: projectConfig.scope,
    });
    changeViewer(_viewer);

    // Is set only if in view-mode. In edit-mode it is awaiting the
    // creation of measurement tools and set in MeasurementContent.tsx
    if (manager && (authMode === "view" || show_toolbar === false))
      manager.setViewer(_viewer, clientConfig);

    return () => {
      changeViewer(undefined);
    };
  }, [projectConfig, clientConfig]);

  useEffect(() => {
    if (!viewer) return;
    const resizeObserver = new ResizeObserver((_) => {
      debouncedResize(viewer);
    });
    resizeObserver.observe(viewer.getContainer());

    if (clientConfig?.render_distance) {
      viewer.getScene().setRenderDistance(clientConfig.render_distance);
    } else {
      viewer.getScene().setRenderDistance(100);
    }

    if (viewer.isNavigable) {
      init_startcollection();
    } else {
      viewer.on("navigablechanged", () => {
        init_startcollection();
      });
    }

    viewer.on("loadingchanged", function (currentLoad: boolean) {
      changeLoading(currentLoad);
    });

    addLayersFromLocalStorage();

    if (clientConfig?.linref?.type) {
      if (userData?.scope.includes("addon/linref")) {
        addLinRefLayers(clientConfig.linref, viewer);
      } else {
        showMessage(
          <>
            {t("alert_warning.linref_no_license")}
            <Link
              href={t("urls.linref.href") || ""}
              target="_blank"
              sx={{
                color: "primary.contrastText",
                textDecoration: "underline",
              }}
            >
              {t("urls.linref.text")}
            </Link>
          </>,
          "warning"
        );
        changeShowAxisscale(false);
      }
    }

    viewer.on("layerupdated", onLayerUpdated);

    return () => {
      viewer.off("layerupdated", onLayerUpdated);
    };
  }, [viewer]);

  const onLayerUpdated = ([layer, updates]: [mm.Layer, any]) => {
    if (
      layer.type === mm.LAYERTYPE.NETWORKLAYER &&
      layer instanceof mm.NetworkLayer
    ) {
      if (
        updates.hasOwnProperty("added") &&
        updates.hasOwnProperty("updated")
      ) {
        if (updates["added"].length > 0 || updates["updated"].length > 0) {
          changeNetworkLayer(layer);
          changeAxisCollection(layer.getAxisCollection());

          const selectedAxis = sidebarStore.getState().selectedAxis;
          if (selectedAxis && !selectedAxis.axis) {
            sidebarStore.setState({ selectedAxis: null });
            layer.featureselector.clearSelection();
          }
        }
      }
    }
  };

  useLayoutEffect(() => {
    if (viewer !== undefined && layersToAdd) {
      addLayers(viewer, layersToAdd);
      changeLayersToAdd(undefined);
    }
  }, [viewer, layersToAdd]);

  // TODO: define what this function should do, in next-gen it should probably be removed completely
  // Reset layers
  // useEffect(() => {
  //   if (viewer !== undefined && resetLayerConfiguration) {
  //     const layers = viewer.getScene().getLayerHandler().layers;
  //     while (layers.length > 0) {
  //       viewer.getScene().getLayerHandler().removeLayer(layers[0]);
  //     }
  //     changeResetLayerConfiguration(false);

  //     // Re-add layers from config and local storage
  //     if (layerConfiguration !== undefined) {
  //       addLayers(viewer, layerConfiguration);
  //       addLayersFromLocalStorage();
  //     }
  //   }
  // }, [resetLayerConfiguration]);

  //change map and resize them
  useEffect(() => {
    if (viewer !== undefined) {
      const map3delement: HTMLElement | null =
        document.getElementById("infra3d");
      const map2delement: HTMLElement | null =
        document.getElementById("infra3dmap");
      const map3dview = document.getElementById("infra3d-container-1");
      const map3dmap = document.getElementById("infra3d-container-2");
      if (!showStreetMap) {
        if (map3delement !== null && map2delement !== null) {
          map3dview?.appendChild(map3delement);
          map3dmap?.appendChild(map2delement);
        }
      } else {
        if (map3delement !== null && map2delement !== null) {
          map3dview?.appendChild(map2delement);
          map3dmap?.appendChild(map3delement);
        }
      }
      viewer.resize();
      const map_component = viewer.getComponent("map");
      if (map_component.activated) {
        map_component.resize();
      }
      const geoframe = viewer.getScene().getCurrentGeoframe();
      if (geoframe) {
        if (geoframe.type !== "cubemap" && geoframe.type !== "calotte") {
          viewer.getScene().getCurrentGeoFrameCollection().fitToCanvas();
        }
      }
      if (!showStreetMap) {
        viewer.getComponent("direction").activate();
      } else {
        viewer.getComponent("direction").deactivate();
      }
    }
  }, [showStreetMap, viewer]);

  //resize component map
  useEffect(() => {
    if (viewer !== undefined) {
      if (mapExpand) {
        const wait = setTimeout(() => {
          viewer.getComponent("map").resize();
        }, 300);
        () => clearTimeout(wait);
      }
    }
  }, [mapExpand]);

  return (
    <Box
      width="100%"
      height="100dvh"
      sx={{ backgroundColor: "black" }}
      display="flex"
      alignItems="center"
      justifyContent="end"
      position={"relative"}
      overflow="hidden"
      id="infra3d-homepage"
    >
      {alertDeleteFeature && <DeleteFeature />}
      {alertSaveLayer && <SaveChanges />}
      {alertSaveOrDiscardLastLayer && <SaveOrDiscardLastChanges />}
      {loading && <Loading />}

      {role !== "api" && <IntroSystem role={role} />}

      <Snackbar
        open={alertMessageState}
        autoHideDuration={5000}
        onClose={closeSnackbar}
        key={alertMessage?.message?.toString()}
      >
        <Alert
          severity={alertMessage?.type}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {alertMessage?.message instanceof Error
            ? alertMessage.message.message
            : alertMessage?.message}
        </Alert>
      </Snackbar>
      <Box position={"absolute"} width="100%" height={"100%"}>
        {viewContainer}
      </Box>
      <Box position={"absolute"} width="100%" height={"100%"}>
        {mapContainer}
      </Box>
      <Box
        id="infra3d-container-1"
        position={"absolute"}
        width="100%"
        height={"100%"}
      >
        {/* Viewer gets rendered here */}
      </Box>
      {children}
    </Box>
  );
}
